import { Flex, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { FileLink, filesApi } from '@texas/api/endpoints/filesApi';
import { organizationsApi } from '@texas/api/endpoints/organizationsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { FileDropzone } from '@texas/components/shared/dropzone/FileDropzone';
import { ImageDropzone } from '@texas/components/shared/dropzone/ImageDropzone';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { useActiveSession } from '@texas/hooks/useSession';
import { ServerError } from '@texas/types';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { acceptedImageFormats } from '@texas/utils/helpers/filesHelper';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateSyncGarp } from './UpdateSyncGarp';

export function Organization({
  organizationId,
  fileId,
  folderId,
}: {
  organizationId: number;
  fileId?: number;
  folderId: number;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const session = useActiveSession();
  const allowEdit = hasRole(session.currentUser.role, [
    Role.SystemAdministrator,
  ]);

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();

  const {
    data: file,
    refetch,
    loading,
    set: setFile,
    error,
  } = useApiResource(filesApi.getFileLink);

  useEffect(() => {
    if (fileId) refetch(fileId);
  }, [refetch, fileId]);

  const { request: updateRequest } = useApiRequest(organizationsApi.updateLogo);

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [organizationId, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          setFile(file);
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [organizationId, setFile, t, toast, updateRequest],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile, maxSize);

  return (
    <ConfigurationPage title={t('organization.title')}>
      <Flex align="flex-start" direction="column" gap={2}>
        <Text variant="header">{t('organization.logo')}</Text>
        {error && <ErrorDetails error={error} />}
        {loading && <Spinner />}
        {file && (
          <ImageDropzone
            imageId={file.id}
            imageIdentifier={file.identifier}
            imageSrc={clientEndpoints.previewImage(file.identifier, 250)}
            imageArchived={false}
            allowEdit={allowEdit}
            onUpload={(files) => uploadFiles(files, folderId)}
            isUploading={isUploading}
            uploadFailed={uploadFailed}
            fileUploads={fileUploads}
            removeFileUpload={removeFileUpload}
            onRemoveFileOpen={onRemoveFileOpen}
          />
        )}
        {allowEdit && !loading && !file && (
          <FileDropzone
            accept={acceptedImageFormats}
            isUploading={isUploading}
            uploadFailed={uploadFailed}
            fileUploads={fileUploads}
            removeFileUpload={removeFileUpload}
            onUpload={(files) => uploadFiles(files, folderId)}
          />
        )}
        <UpdateSyncGarp />
      </Flex>
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeFile')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={organizationsApi.deleteLogo}
        args={[organizationId]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => setFile(null)}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
    </ConfigurationPage>
  );
}
